import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import type {
  AppState,
  GameEvent,
  IdentityChoice,
  Player,
  Sanction,
  SanctionType,
  SessionView,
  StreamMessage,
  Trigger,
} from "../shared/types";
import {
  ApiError,
  api,
  friendlyError,
  type HistoryPage,
  type PlayerInput,
  type SanctionInput,
  type SanctionTypeInput,
  type TriggerInput,
} from "./api";
import binglogosLogoUrl from "./assets/binglogos-logo.png";
import quiGifUrl from "./assets/qui.gif";
import quiStillUrl from "./assets/qui-still.png";
import rabbiBlueUrl from "./assets/rabbi-blue.webp";
import rabbiGoldUrl from "./assets/rabbi-gold.webp";
import { playSanctionEffects, primeSanctionEffects } from "./sanction-effects";

type View = "game" | "history" | "settings";

const randomId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const byNewest = (a: GameEvent, b: GameEvent) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

function mostRecentEvent(state: AppState): GameEvent | null {
  return [...state.events].sort(byNewest).find((event) => !event.cancelledAt) ?? null;
}

export default function App() {
  const [session, setSession] = useState<SessionView | null>(null);
  const [state, setState] = useState<AppState | null>(null);
  const stateRevision = useRef(-1);
  const [booting, setBooting] = useState(true);
  const [view, setView] = useState<View>("game");
  const [choosingIdentity, setChoosingIdentity] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [streamConnected, setStreamConnected] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latestResult, setLatestResult] = useState<GameEvent | null>(null);
  const knownEventIds = useRef<Set<string> | null>(null);

  const applyState = useCallback((nextState: AppState): boolean => {
    if (nextState.revision < stateRevision.current) return false;
    stateRevision.current = nextState.revision;
    setState(nextState);
    return true;
  }, []);

  const clearState = useCallback(() => {
    stateRevision.current = -1;
    knownEventIds.current = null;
    setState(null);
  }, []);

  const clearAuthentication = useCallback(() => {
    setSession(null);
    clearState();
    setLatestResult(null);
    setChoosingIdentity(false);
    setView("game");
  }, [clearState]);

  const loadSession = useCallback(async () => {
    try {
      const nextSession = await api.session();
      setSession(nextSession);
      return nextSession;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        clearAuthentication();
        return null;
      }
      throw caught;
    }
  }, [clearAuthentication]);

  const loadState = useCallback(async () => {
    try {
      const nextState = await api.state();
      applyState(nextState);
      return nextState;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        clearAuthentication();
        return null;
      }
      throw caught;
    }
  }, [applyState, clearAuthentication]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const initialSession = await loadSession();
        if (initialSession?.currentPlayer) await loadState();
      } catch (caught) {
        if (alive) setError(friendlyError(caught));
      } finally {
        if (alive) setBooting(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadSession, loadState]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    const prime = () => primeSanctionEffects();
    window.addEventListener("pointerdown", prime, { capture: true, passive: true });
    window.addEventListener("pointerup", prime, { capture: true, passive: true });
    window.addEventListener("keydown", prime, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", prime, { capture: true });
      window.removeEventListener("pointerup", prime, { capture: true });
      window.removeEventListener("keydown", prime, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!session) return;

    const stream = new EventSource("/stream");
    const handleMessage = (event: MessageEvent<string>) => {
      let message: StreamMessage;
      try {
        message = JSON.parse(event.data) as StreamMessage;
      } catch {
        return;
      }

      setStreamConnected(true);
      if (message.type === "identity_revoked") {
        void loadSession()
          .then((freshSession) => {
            if (!freshSession?.currentPlayer) {
              setLatestResult(null);
              setNotice("Ton joueur a été repris sur un autre appareil.");
            }
          })
          .catch((caught: unknown) => setError(friendlyError(caught)));
      } else if (message.type === "state_changed" || message.type === "connected") {
        void Promise.all([loadState(), loadSession()]).catch((caught: unknown) =>
          setError(friendlyError(caught)),
        );
      }
    };

    stream.onopen = () => setStreamConnected(true);
    stream.onmessage = handleMessage;
    stream.onerror = () => {
      setStreamConnected(false);
      // EventSource does not expose the HTTP status. Probe the session so an
      // expired cookie returns the app to the login screen, while transient
      // network failures keep the automatic SSE reconnection alive.
      void loadSession().catch(() => undefined);
    };
    for (const type of ["connected", "state_changed", "identity_revoked"]) {
      stream.addEventListener(type, handleMessage as EventListener);
    }

    return () => {
      stream.close();
      setStreamConnected(false);
    };
  }, [session?.expiresAt, loadSession, loadState]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(null), 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    setLatestResult((current) => {
      if (!current || !state) return current;
      const fresh = state.events.find((event) => event.id === current.id);
      if (!fresh || fresh.cancelledAt || fresh.gameId !== state.currentGame.id) return null;
      return fresh;
    });
  }, [state?.events]);

  useEffect(() => {
    if (!state) {
      knownEventIds.current = null;
      return;
    }
    const nextIds = new Set(state.events.map((event) => event.id));
    const previousIds = knownEventIds.current;
    knownEventIds.current = nextIds;
    if (previousIds === null) return;
    const unseen = state.events
      .filter((event) => !previousIds.has(event.id) && !event.cancelledAt)
      .sort(byNewest);
    if (unseen.length === 0) return;
    setLatestResult(unseen[0]!);
    unseen.reverse().forEach((_, index) => playSanctionEffects(index * 700));
  }, [state]);

  const handleLogin = async (password: string) => {
    const nextSession = await api.login(password);
    setSession(nextSession);
    setError(null);
    if (nextSession.currentPlayer) applyState(await api.state());
  };

  const handleChoosePlayer = async (playerId: string, takeover: boolean) => {
    if (!session) return;
    const nextSession = await api.choosePlayer(
      session.csrfToken,
      playerId,
      takeover,
    );
    setSession(nextSession);
    applyState(await api.state());
    setChoosingIdentity(false);
    setView("game");
    setError(null);
  };

  const handleLogout = async () => {
    try {
      if (session) await api.logout(session.csrfToken);
    } catch (caught) {
      // An already-expired server session is still a successful local logout.
      if (!(caught instanceof ApiError && caught.status === 401)) throw caught;
    }
    clearAuthentication();
  };

  const refreshAll = useCallback(async () => {
    await Promise.all([loadState(), loadSession()]);
  }, [loadSession, loadState]);

  if (booting) return <SplashScreen />;
  if (!session) {
    return <LoginScreen onLogin={handleLogin} externalError={error} />;
  }
  if (!session.currentPlayer || session.identityRequired || choosingIdentity) {
    return (
      <IdentityScreen
        players={session.players}
        onChoose={handleChoosePlayer}
        onLogout={handleLogout}
        onCancel={session.currentPlayer && !session.identityRequired ? () => setChoosingIdentity(false) : undefined}
        externalError={error}
      />
    );
  }
  if (!state) return <SplashScreen />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="brand"
          type="button"
          onClick={() => setView("game")}
          aria-label="Binglogos — retour au jeu"
        >
          <img className="app-logo app-logo--brand" src={binglogosLogoUrl} alt="" />
          <strong>Binglogos</strong>
        </button>
        <div className="topbar-actions">
          <button
            className="player-chip"
            type="button"
            onClick={() => setChoosingIdentity(true)}
            aria-label={`Joueur actuel : ${session.currentPlayer.name}. Changer de joueur`}
          >
            <PlayerAvatar
              color={session.currentPlayer.color}
              portraitHint={session.currentPlayer.emoji}
              variant={session.currentPlayer.id}
              size="mini"
            />
            <span>{session.currentPlayer.name}</span>
            <span aria-hidden="true">⌄</span>
          </button>
        </div>
      </header>

      {(!online || !streamConnected) && (
        <div className="offline-banner" role="status">
          <span aria-hidden="true">☁︎</span>
          {online ? "Synchronisation interrompue" : "Connexion perdue"} — les tirages sont en pause
        </div>
      )}

      <main className="page" id="main-content">
        {view === "game" && (
          <GameView
            state={state}
            online={online && streamConnected}
            csrfToken={session.csrfToken}
            latestResult={latestResult}
            onResult={setLatestResult}
            onState={applyState}
            onError={(caught) => setError(friendlyError(caught))}
            onOpenSettings={() => setView("settings")}
          />
        )}
        {view === "history" && (
          <HistoryView currentEvents={state.events} />
        )}
        {view === "settings" && (
          <SettingsView
            state={state}
            csrfToken={session.csrfToken}
            onRefresh={refreshAll}
            onLogout={handleLogout}
            onError={(caught) => setError(friendlyError(caught))}
            onNotice={setNotice}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="Navigation principale">
        <NavButton active={view === "game"} icon="▦" label="Jeu" onClick={() => setView("game")} />
        <NavButton active={view === "history"} icon="↺" label="Historique" onClick={() => setView("history")} />
        <NavButton active={view === "settings"} icon="⚙" label="Réglages" onClick={() => setView("settings")} />
      </nav>

      {notice && <Toast kind="notice" message={notice} onClose={() => setNotice(null)} />}
      {error && <Toast kind="error" message={error} onClose={() => setError(null)} />}
    </div>
  );
}

function SplashScreen() {
  return (
    <main className="center-screen splash-screen">
      <img className="app-logo app-logo--splash" src={binglogosLogoUrl} alt="Binglogos" />
      <span className="loading-dots" aria-label="Chargement"><i /><i /><i /></span>
    </main>
  );
}

function LoginScreen({
  onLogin,
  externalError,
}: {
  onLogin: (password: string) => Promise<void>;
  externalError: string | null;
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onLogin(password);
    } catch (caught) {
      setError(friendlyError(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-screen">
      <div className="decor decor-one" aria-hidden="true">★</div>
      <div className="decor decor-two" aria-hidden="true">✦</div>
      <section className="auth-card" aria-labelledby="login-title">
        <img className="app-logo app-logo--hero" src={binglogosLogoUrl} alt="" />
        <h1 id="login-title">Binglogos</h1>
        <form onSubmit={submit}>
          <label htmlFor="password">Mot de passe</label>
          <div className="password-field">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPassword ? "Masquer" : "Voir"}
            </button>
          </div>
          {(error ?? externalError) && <p className="form-error" role="alert">{error ?? externalError}</p>}
          <button className="primary-button full-width" disabled={submitting || !password}>
            {submitting ? "Connexion…" : "Entrer"}
          </button>
        </form>
      </section>
    </main>
  );
}

const rabbiPortraits = [rabbiBlueUrl, rabbiGoldUrl] as const;

function rabbiPortraitFor(variant: string, portraitHint: string): string {
  if (portraitHint === "🧔🏻‍♂️") return rabbiBlueUrl;
  if (portraitHint === "🧔🏽‍♂️") return rabbiGoldUrl;
  let hash = 0;
  for (const character of variant) {
    hash = (Math.imul(hash, 31) + character.codePointAt(0)!) >>> 0;
  }
  return rabbiPortraits[hash % rabbiPortraits.length]!;
}

function PlayerAvatar({ color, portraitHint, variant, size }: {
  color: string;
  portraitHint: string;
  variant: string;
  size: "mini" | "large" | "result" | "settings";
}) {
  return (
    <span
      className={`rabbi-avatar rabbi-avatar--${size}`}
      style={{ "--avatar-color": color } as React.CSSProperties}
      aria-hidden="true"
    >
      <img
        className="rabbi-avatar__image"
        src={rabbiPortraitFor(variant, portraitHint)}
        alt=""
        draggable={false}
      />
    </span>
  );
}

function IdentityScreen({
  players,
  onChoose,
  onLogout,
  onCancel,
  externalError,
}: {
  players: IdentityChoice[];
  onChoose: (playerId: string, takeover: boolean) => Promise<void>;
  onLogout: () => Promise<void>;
  onCancel?: () => void;
  externalError: string | null;
}) {
  const [takingOver, setTakingOver] = useState<IdentityChoice | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activePlayers = players.filter((player) => player.active && !player.archivedAt);

  const choose = async (player: IdentityChoice, takeover = false) => {
    if (player.claimedByAnotherSession && !takeover) {
      setTakingOver(player);
      return;
    }
    setSubmittingId(player.id);
    setError(null);
    try {
      await onChoose(player.id, takeover);
    } catch (caught) {
      if (
        !takeover &&
        caught instanceof ApiError &&
        caught.code === "IDENTITY_ALREADY_CLAIMED"
      ) {
        setTakingOver(player);
        setError(null);
      } else {
        setError(friendlyError(caught));
      }
    } finally {
      setSubmittingId(null);
      if (takeover) setTakingOver(null);
    }
  };

  return (
    <main className="identity-screen">
      <section className="identity-panel" aria-labelledby="identity-title">
        <span className="identity-star identity-star-left" aria-hidden="true">★</span>
        <span className="identity-star identity-star-right" aria-hidden="true">★</span>
        <picture>
          <source media="(prefers-reduced-motion: reduce)" srcSet={quiStillUrl} />
          <img
            className="identity-gif"
            src={quiGifUrl}
            width="220"
            height="188"
            alt="Un homme demande : « Qui ? »"
          />
        </picture>
        <h1 id="identity-title">Tu es qui&nbsp;?</h1>
        <div className="identity-grid">
          {activePlayers.map((player) => {
            const status = submittingId === player.id
              ? "Connexion…"
              : player.claimedByAnotherSession
                ? "Déjà pris"
                : player.claimedByThisSession
                  ? "Toi"
                  : null;
            return (
              <button
                key={player.id}
                className={`identity-card ${player.claimedByAnotherSession ? "is-claimed" : ""}`}
                type="button"
                disabled={submittingId !== null}
                onClick={() => void choose(player)}
              >
                <PlayerAvatar color={player.color} portraitHint={player.emoji} variant={player.id} size="large" />
                <strong>{player.name}</strong>
                {status && <small>{status}</small>}
              </button>
            );
          })}
        </div>
        {activePlayers.length === 0 && (
          <div className="empty-state compact"><span aria-hidden="true">🫥</span><p>Aucun joueur actif.</p></div>
        )}
        {(error ?? externalError) && <p className="form-error" role="alert">{error ?? externalError}</p>}
        <div className="identity-actions">
          {onCancel && <button className="text-button" type="button" onClick={onCancel}>Retour au jeu</button>}
          <button className="text-button" type="button" onClick={() => void onLogout().catch((caught: unknown) => setError(friendlyError(caught)))}>Se déconnecter</button>
        </div>
      </section>

      {takingOver && (
        <ConfirmDialog
          title={`Reprendre ${takingOver.name} ?`}
          confirmLabel="Prendre cette identité"
          tone="warning"
          onCancel={() => setTakingOver(null)}
          onConfirm={() => void choose(takingOver, true)}
        >
          L’autre appareil devra choisir un joueur.
        </ConfirmDialog>
      )}
    </main>
  );
}

function NavButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button className={active ? "active" : ""} type="button" aria-current={active ? "page" : undefined} onClick={onClick}>
      <span aria-hidden="true">{icon}</span>
      {label}
    </button>
  );
}

function GameView({
  state,
  online,
  csrfToken,
  latestResult,
  onResult,
  onState,
  onError,
  onOpenSettings,
}: {
  state: AppState;
  online: boolean;
  csrfToken: string;
  latestResult: GameEvent | null;
  onResult: (event: GameEvent | null) => void;
  onState: (state: AppState) => boolean;
  onError: (error: unknown) => void;
  onOpenSettings: () => void;
}) {
  const [speaker, setSpeaker] = useState("*");
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  const [undoing, setUndoing] = useState(false);
  const [showNewGame, setShowNewGame] = useState(false);
  const [startingGame, setStartingGame] = useState(false);
  const lastTriggerAt = useRef(new Map<string, number>());

  const sanctionById = useMemo(
    () => new Map(state.sanctions.map((sanction) => [sanction.id, sanction])),
    [state.sanctions],
  );
  const typeById = useMemo(
    () => new Map(state.sanctionTypes.map((type) => [type.id, type])),
    [state.sanctionTypes],
  );
  const triggers = useMemo(
    () =>
      state.triggers
        .filter((trigger) => trigger.active && !trigger.archivedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.triggers],
  );
  const speakers = useMemo(
    () => [...new Set(triggers.map((trigger) => trigger.speaker.trim()).filter(Boolean))],
    [triggers],
  );
  const filteredTriggers = speaker === "*" ? triggers : triggers.filter((trigger) => trigger.speaker === speaker);

  useEffect(() => {
    if (speaker !== "*" && !speakers.includes(speaker)) setSpeaker("*");
  }, [speaker, speakers]);

  const triggerEvent = async (triggerId: string) => {
    if (!online || pending.has(triggerId)) return;
    const timestamp = performance.now();
    const previousTimestamp = lastTriggerAt.current.get(triggerId) ?? -Infinity;
    if (timestamp - previousTimestamp < 650) return;
    lastTriggerAt.current.set(triggerId, timestamp);
    setPending((current) => new Set(current).add(triggerId));
    try {
      const nextState = await api.trigger(csrfToken, triggerId, randomId());
      if (onState(nextState)) onResult(mostRecentEvent(nextState));
    } catch (caught) {
      onError(caught);
    } finally {
      setPending((current) => {
        const next = new Set(current);
        next.delete(triggerId);
        return next;
      });
    }
  };

  const undo = async () => {
    if (!state.canUndoEventId || undoing) return;
    setUndoing(true);
    try {
      const nextState = await api.undo(csrfToken, state.canUndoEventId);
      if (onState(nextState)) onResult(null);
    } catch (caught) {
      onError(caught);
    } finally {
      setUndoing(false);
    }
  };

  const newGame = async () => {
    setStartingGame(true);
    try {
      const nextState = await api.newGame(csrfToken, randomId());
      if (onState(nextState)) onResult(null);
      setShowNewGame(false);
    } catch (caught) {
      onError(caught);
    } finally {
      setStartingGame(false);
    }
  };

  return (
    <section className="game-view" aria-labelledby="game-title">
      <div className="page-heading game-heading">
        <h1 id="game-title">Jeu</h1>
        <button className="secondary-button small" type="button" onClick={() => setShowNewGame(true)}>Nouvelle partie</button>
      </div>

      {speakers.length > 0 && (
        <div className="filter-row" aria-label="Filtrer par intervenant">
          <button className={speaker === "*" ? "active" : ""} onClick={() => setSpeaker("*")} type="button">Tout</button>
          {speakers.map((name) => (
            <button key={name} className={speaker === name ? "active" : ""} onClick={() => setSpeaker(name)} type="button">{name}</button>
          ))}
        </div>
      )}

      {filteredTriggers.length > 0 ? (
        <div className="trigger-grid">
          {filteredTriggers.map((trigger) => {
            const sanction = sanctionById.get(trigger.sanctionId);
            const type = sanction ? typeById.get(sanction.typeId) : undefined;
            const isPending = pending.has(trigger.id);
            return (
              <button
                className="trigger-card"
                key={trigger.id}
                type="button"
                disabled={!online || isPending || !sanction}
                onClick={() => void triggerEvent(trigger.id)}
                style={{ "--card-accent": type?.color ?? "#5aa7ff" } as React.CSSProperties}
                aria-label={`${trigger.label}. Sanction : ${sanction?.label ?? "indisponible"}. ${state.triggerCounts[trigger.id] ?? 0} fois`}
              >
                <span className="trigger-topline">
                  <span className="type-emoji" aria-hidden="true">{isPending ? "⋯" : (type?.emoji ?? "⚡")}</span>
                  <span className="hit-count">×{state.triggerCounts[trigger.id] ?? 0}</span>
                </span>
                <strong>{trigger.label}</strong>
                {trigger.speaker && <small className="speaker-label">{trigger.speaker}</small>}
                <span className="sanction-label">{sanction?.label ?? "Sanction indisponible"}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <span aria-hidden="true">🎯</span>
          <h2>Pas encore de cases</h2>
          <button className="primary-button" type="button" onClick={onOpenSettings}>Créer les cases</button>
        </div>
      )}

      {latestResult && !latestResult.cancelledAt && (
        <ResultPanel
          event={latestResult}
          targetColor={state.players.find((player) => player.id === latestResult.targetPlayerId)?.color}
          canUndo={state.canUndoEventId === latestResult.id}
          undoing={undoing}
          onClose={() => onResult(null)}
          onUndo={() => void undo()}
        />
      )}

      {showNewGame && (
        <ConfirmDialog
          title="Nouvelle partie ?"
          confirmLabel={startingGame ? "Création…" : "Repartir à zéro"}
          onCancel={() => setShowNewGame(false)}
          onConfirm={() => void newGame()}
          disabled={startingGame}
        >
          Les compteurs repartent à zéro. L’historique est conservé.
        </ConfirmDialog>
      )}
    </section>
  );
}

function ResultPanel({
  event,
  targetColor = "#2f80ed",
  canUndo,
  undoing,
  onClose,
  onUndo,
}: {
  event: GameEvent;
  targetColor?: string;
  canUndo: boolean;
  undoing: boolean;
  onClose: () => void;
  onUndo: () => void;
}) {
  return (
    <aside className="result-panel" aria-live="assertive" aria-label={`Sanction pour ${event.targetName}`}>
      <div className="confetti" aria-hidden="true"><i /><i /><i /><i /><i /></div>
      <button className="close-button" type="button" onClick={onClose} aria-label="Fermer le résultat">×</button>
      <PlayerAvatar color={targetColor} portraitHint={event.targetEmoji} variant={event.targetPlayerId} size="result" />
      <div className="result-copy">
        <small>Sanction pour</small>
        <strong>{event.targetName}</strong>
        <p><span aria-hidden="true">{event.sanctionTypeEmoji}</span> {event.sanctionLabel}</p>
      </div>
      {canUndo && (
        <button className="undo-button" type="button" disabled={undoing} onClick={onUndo}>
          ↶ {undoing ? "Annulation…" : "Annuler"}
        </button>
      )}
    </aside>
  );
}

function HistoryView({ currentEvents }: { currentEvents: GameEvent[] }) {
  const currentEventsRef = useRef(currentEvents);
  const [page, setPage] = useState<HistoryPage>({
    items: [...currentEvents].sort(byNewest),
    nextBefore: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mergeEvents = (...collections: GameEvent[][]): GameEvent[] => {
    const merged = new Map<string, GameEvent>();
    for (const collection of collections) {
      for (const event of collection) merged.set(event.id, event);
    }
    return [...merged.values()].sort(byNewest);
  };

  useEffect(() => {
    currentEventsRef.current = currentEvents;
    setPage((current) => ({
      ...current,
      items: mergeEvents(current.items, currentEvents),
    }));
  }, [currentEvents]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void api.history()
      .then((history) => {
        if (alive) {
          setPage({
            ...history,
            items: mergeEvents(history.items, currentEventsRef.current),
          });
        }
      })
      .catch((caught: unknown) => {
        if (alive) setError(friendlyError(caught));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadMore = async () => {
    if (!page.nextBefore) return;
    setLoadingMore(true);
    try {
      const next = await api.history(page.nextBefore);
      setPage({
        items: mergeEvents(page.items, next.items, currentEventsRef.current),
        nextBefore: next.nextBefore,
      });
    } catch (caught) {
      setError(friendlyError(caught));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section aria-labelledby="history-title">
      <div className="page-heading">
        <h1 id="history-title">Historique</h1>
        {page.items.some((event) => !event.cancelledAt) && (
          <span className="total-pill">{page.items.filter((event) => !event.cancelledAt).length} tirages</span>
        )}
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      {loading ? (
        <HistorySkeleton />
      ) : page.items.length === 0 ? (
        <div className="empty-state"><span aria-hidden="true">🗒️</span><h2>Aucun tirage</h2></div>
      ) : (
        <div className="timeline">
          {page.items.map((event) => <HistoryEventRow event={event} key={event.id} />)}
        </div>
      )}
      {page.nextBefore && (
        <button className="secondary-button full-width load-more" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? "Chargement…" : "Voir les tirages précédents"}
        </button>
      )}
    </section>
  );
}

function HistoryEventRow({ event }: { event: GameEvent }) {
  const date = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(new Date(event.createdAt));
  return (
    <article className={`history-event ${event.cancelledAt ? "is-cancelled" : ""}`}>
      <div className="timeline-dot" aria-hidden="true">{event.targetEmoji}</div>
      <div className="history-body">
        <div className="history-meta"><time dateTime={event.createdAt}>{date}</time><span>{event.triggerLabel}</span></div>
        <p>Sanction pour <strong>{event.targetName}</strong></p>
        <div className="history-sanction"><span aria-hidden="true">{event.sanctionTypeEmoji}</span>{event.sanctionLabel}</div>
        {event.cancelledAt && <small className="cancelled-label">Annulé{event.undoneByName ? ` par ${event.undoneByName}` : ""}</small>}
      </div>
    </article>
  );
}

function HistorySkeleton() {
  return <div className="timeline skeleton" aria-label="Chargement"><i /><i /><i /></div>;
}

type SettingsSection = "players" | "types" | "sanctions" | "triggers";

function SettingsView({
  state,
  csrfToken,
  onRefresh,
  onLogout,
  onError,
  onNotice,
}: {
  state: AppState;
  csrfToken: string;
  onRefresh: () => Promise<void>;
  onLogout: () => Promise<void>;
  onError: (error: unknown) => void;
  onNotice: (message: string) => void;
}) {
  const [section, setSection] = useState<SettingsSection>("players");
  const [busy, setBusy] = useState(false);

  const mutate = async (operation: () => Promise<unknown>, success: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await operation();
      await onRefresh();
      onNotice(success);
    } catch (caught) {
      onError(caught);
      if (caught instanceof ApiError && caught.status === 409) await onRefresh();
      throw caught;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="settings-title">
      <div className="page-heading"><h1 id="settings-title">Réglages</h1></div>
      <div className="settings-tabs" role="tablist" aria-label="Catégories de réglages">
        <SettingsTab active={section === "players"} icon="🧑" label="Joueurs" onClick={() => setSection("players")} />
        <SettingsTab active={section === "types"} icon="🎨" label="Types" onClick={() => setSection("types")} />
        <SettingsTab active={section === "sanctions"} icon="⚡" label="Sanctions" onClick={() => setSection("sanctions")} />
        <SettingsTab active={section === "triggers"} icon="🎯" label="Cases" onClick={() => setSection("triggers")} />
      </div>

      {section === "players" && <PlayersSettings players={state.players} busy={busy} mutate={mutate} csrfToken={csrfToken} />}
      {section === "types" && <TypesSettings types={state.sanctionTypes} busy={busy} mutate={mutate} csrfToken={csrfToken} />}
      {section === "sanctions" && <SanctionsSettings sanctions={state.sanctions} types={state.sanctionTypes} busy={busy} mutate={mutate} csrfToken={csrfToken} />}
      {section === "triggers" && <TriggersSettings triggers={state.triggers} sanctions={state.sanctions} types={state.sanctionTypes} busy={busy} mutate={mutate} csrfToken={csrfToken} />}

      <div className="account-actions">
        <button className="text-button danger-text" type="button" onClick={() => void onLogout().catch(onError)}>Se déconnecter</button>
      </div>
    </section>
  );
}

function SettingsTab({ active, icon, label, onClick }: { active: boolean; icon: string; label: string; onClick: () => void }) {
  return <button role="tab" aria-selected={active} className={active ? "active" : ""} type="button" onClick={onClick}><span aria-hidden="true">{icon}</span>{label}</button>;
}

interface SettingsMutation {
  (operation: () => Promise<unknown>, success: string): Promise<void>;
}

const defaultPlayer: PlayerInput = { name: "", emoji: "🧔🏼‍♂️", color: "#5b9cff", active: true };

function PlayersSettings({ players, busy, mutate, csrfToken }: { players: Player[]; busy: boolean; mutate: SettingsMutation; csrfToken: string }) {
  const [editing, setEditing] = useState<Player | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<PlayerInput>(defaultPlayer);
  const openAdd = () => { setEditing(null); setDraft(defaultPlayer); setAdding(true); };
  const openEdit = (player: Player) => { setEditing(player); setDraft({ name: player.name, emoji: player.emoji, color: player.color, active: player.active }); setAdding(true); };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await mutate(() => editing ? api.updatePlayer(csrfToken, editing, draft) : api.createPlayer(csrfToken, draft), editing ? "Joueur mis à jour" : "Joueur ajouté");
      setAdding(false);
    } catch { /* Le parent affiche l’erreur. */ }
  };
  return (
    <SettingsList title="Les joueurs" addLabel="Ajouter un joueur" onAdd={openAdd}>
      {players.filter((player) => !player.archivedAt).map((player) => (
        <SettingsRow key={player.id} avatar={player} title={player.name} subtitle={player.active ? "Actif" : "En pause"} onEdit={() => openEdit(player)} onArchive={() => void mutate(() => api.archivePlayer(csrfToken, player), "Joueur archivé").catch(() => undefined)} busy={busy} />
      ))}
      {adding && (
        <EditorDialog title={editing ? "Modifier le joueur" : "Nouveau joueur"} onCancel={() => setAdding(false)} onSubmit={save} busy={busy}>
          <div className="player-editor-identity"><PlayerAvatar color={draft.color} portraitHint={draft.emoji} variant={editing?.id ?? "new-player"} size="settings" /><Field label="Prénom"><input value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required autoFocus /></Field></div>
          <Field label="Couleur"><div className="color-field"><input type="color" value={draft.color} onChange={(event) => setDraft({ ...draft, color: event.target.value })} /></div></Field>
          <label className="switch-row"><strong>Inclus dans les tirages</strong><input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /></label>
        </EditorDialog>
      )}
    </SettingsList>
  );
}

const defaultType: SanctionTypeInput = { name: "", emoji: "🥤", color: "#5aa7ff" };

function TypesSettings({ types, busy, mutate, csrfToken }: { types: SanctionType[]; busy: boolean; mutate: SettingsMutation; csrfToken: string }) {
  const [editing, setEditing] = useState<SanctionType | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SanctionTypeInput>(defaultType);
  const edit = (type?: SanctionType) => { setEditing(type ?? null); setDraft(type ? { name: type.name, emoji: type.emoji, color: type.color } : defaultType); setOpen(true); };
  const save = async (event: FormEvent) => { event.preventDefault(); try { await mutate(() => editing ? api.updateSanctionType(csrfToken, editing, draft) : api.createSanctionType(csrfToken, draft), editing ? "Type mis à jour" : "Type ajouté"); setOpen(false); } catch { /* Le parent affiche l’erreur. */ } };
  return (
    <SettingsList title="Types de sanctions" addLabel="Ajouter un type" onAdd={() => edit()}>
      {types.filter((type) => !type.archivedAt).map((type) => <SettingsRow key={type.id} icon={type.emoji} color={type.color} title={type.name} subtitle="" onEdit={() => edit(type)} onArchive={() => void mutate(() => api.archiveSanctionType(csrfToken, type), "Type archivé").catch(() => undefined)} busy={busy} />)}
      {open && <EditorDialog title={editing ? "Modifier le type" : "Nouveau type"} onCancel={() => setOpen(false)} onSubmit={save} busy={busy}><div className="field-pair emoji-name"><Field label="Emoji"><input value={draft.emoji} maxLength={8} onChange={(event) => setDraft({ ...draft, emoji: event.target.value })} required /></Field><Field label="Nom"><input value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required autoFocus /></Field></div><Field label="Couleur"><div className="color-field"><input type="color" value={draft.color} onChange={(event) => setDraft({ ...draft, color: event.target.value })} /></div></Field></EditorDialog>}
    </SettingsList>
  );
}

function SanctionsSettings({ sanctions, types, busy, mutate, csrfToken }: { sanctions: Sanction[]; types: SanctionType[]; busy: boolean; mutate: SettingsMutation; csrfToken: string }) {
  const activeTypes = types.filter((type) => !type.archivedAt);
  const makeDefault = (): SanctionInput => ({ typeId: activeTypes[0]?.id ?? "", label: "", description: "" });
  const [editing, setEditing] = useState<Sanction | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SanctionInput>(makeDefault);
  const edit = (sanction?: Sanction) => { setEditing(sanction ?? null); setDraft(sanction ? { typeId: sanction.typeId, label: sanction.label, description: sanction.description } : makeDefault()); setOpen(true); };
  const typeById = new Map(types.map((type) => [type.id, type]));
  const save = async (event: FormEvent) => { event.preventDefault(); try { await mutate(() => editing ? api.updateSanction(csrfToken, editing, draft) : api.createSanction(csrfToken, draft), editing ? "Sanction mise à jour" : "Sanction ajoutée"); setOpen(false); } catch { /* Le parent affiche l’erreur. */ } };
  return (
    <SettingsList title="Les sanctions" addLabel="Ajouter une sanction" onAdd={() => edit()} addDisabled={activeTypes.length === 0}>
      {sanctions.filter((sanction) => !sanction.archivedAt).map((sanction) => { const type = typeById.get(sanction.typeId); return <SettingsRow key={sanction.id} icon={type?.emoji ?? "⚡"} color={type?.color} title={sanction.label} subtitle={[type?.name, sanction.description].filter(Boolean).join(" · ")} onEdit={() => edit(sanction)} onArchive={() => void mutate(() => api.archiveSanction(csrfToken, sanction), "Sanction archivée").catch(() => undefined)} busy={busy} />; })}
      {activeTypes.length === 0 && <p className="helper-warning">Crée d’abord un type de sanction.</p>}
      {open && <EditorDialog title={editing ? "Modifier la sanction" : "Nouvelle sanction"} onCancel={() => setOpen(false)} onSubmit={save} busy={busy}><Field label="Type"><select value={draft.typeId} onChange={(event) => setDraft({ ...draft, typeId: event.target.value })} required>{activeTypes.map((type) => <option value={type.id} key={type.id}>{type.emoji} {type.name}</option>)}</select></Field><Field label="Sanction"><input value={draft.label} maxLength={80} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="Ex. 2 gorgées" required autoFocus /></Field><Field label="Petit détail (facultatif)"><textarea value={draft.description} maxLength={180} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="Une précision utile au moment du tirage" /></Field></EditorDialog>}
    </SettingsList>
  );
}

function TriggersSettings({ triggers, sanctions, types, busy, mutate, csrfToken }: { triggers: Trigger[]; sanctions: Sanction[]; types: SanctionType[]; busy: boolean; mutate: SettingsMutation; csrfToken: string }) {
  const activeSanctions = sanctions.filter((sanction) => !sanction.archivedAt);
  const makeDefault = (): TriggerInput => ({ label: "", speaker: "", sanctionId: activeSanctions[0]?.id ?? "", active: true, sortOrder: Math.max(0, ...triggers.map((trigger) => trigger.sortOrder)) + 10 });
  const [editing, setEditing] = useState<Trigger | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TriggerInput>(makeDefault);
  const edit = (trigger?: Trigger) => { setEditing(trigger ?? null); setDraft(trigger ? { label: trigger.label, speaker: trigger.speaker, sanctionId: trigger.sanctionId, active: trigger.active, sortOrder: trigger.sortOrder } : makeDefault()); setOpen(true); };
  const sanctionById = new Map(sanctions.map((sanction) => [sanction.id, sanction]));
  const typeById = new Map(types.map((type) => [type.id, type]));
  const save = async (event: FormEvent) => { event.preventDefault(); try { await mutate(() => editing ? api.updateTrigger(csrfToken, editing, draft) : api.createTrigger(csrfToken, draft), editing ? "Case mise à jour" : "Case ajoutée"); setOpen(false); } catch { /* Le parent affiche l’erreur. */ } };
  return (
    <SettingsList title="Les cases du bingo" addLabel="Ajouter une case" onAdd={() => edit()} addDisabled={activeSanctions.length === 0}>
      {triggers.filter((trigger) => !trigger.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder).map((trigger) => { const sanction = sanctionById.get(trigger.sanctionId); const type = sanction ? typeById.get(sanction.typeId) : undefined; return <SettingsRow key={trigger.id} icon={type?.emoji ?? "🎯"} color={type?.color} title={trigger.label} subtitle={[trigger.speaker || "Tout intervenant", sanction?.label, trigger.active ? null : "En pause"].filter(Boolean).join(" · ")} onEdit={() => edit(trigger)} onArchive={() => void mutate(() => api.archiveTrigger(csrfToken, trigger), "Case archivée").catch(() => undefined)} busy={busy} />; })}
      {activeSanctions.length === 0 && <p className="helper-warning">Crée d’abord une sanction.</p>}
      {open && <EditorDialog title={editing ? "Modifier la case" : "Nouvelle case"} onCancel={() => setOpen(false)} onSubmit={save} busy={busy}><Field label="Phrase ou événement"><input value={draft.label} maxLength={100} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="Ex. On ne peut plus rien dire" required autoFocus /></Field><Field label="Intervenant (facultatif)"><input value={draft.speaker} maxLength={50} onChange={(event) => setDraft({ ...draft, speaker: event.target.value })} placeholder="Ex. Soral" /></Field><Field label="Sanction"><select value={draft.sanctionId} onChange={(event) => setDraft({ ...draft, sanctionId: event.target.value })} required>{activeSanctions.map((sanction) => { const type = typeById.get(sanction.typeId); return <option value={sanction.id} key={sanction.id}>{type?.emoji} {sanction.label}</option>; })}</select></Field><Field label="Position dans la grille"><input type="number" min="0" max="10000" value={draft.sortOrder} onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })} required /></Field><label className="switch-row"><strong>Visible en jeu</strong><input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /></label></EditorDialog>}
    </SettingsList>
  );
}

function SettingsList({ title, addLabel, onAdd, addDisabled = false, children }: { title: string; addLabel: string; onAdd: () => void; addDisabled?: boolean; children: ReactNode }) {
  return <div className="settings-section"><div className="settings-section-heading"><h2 className="sr-only">{title}</h2><button className="primary-button small" type="button" disabled={addDisabled} onClick={onAdd}>＋ {addLabel}</button></div><div className="settings-list">{children}</div></div>;
}

function SettingsRow({ icon = "★", color = "#174f9f", avatar, title, subtitle, onEdit, onArchive, busy }: { icon?: string; color?: string; avatar?: Player; title: string; subtitle: string; onEdit: () => void; onArchive: () => void; busy: boolean }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <article className="settings-row">
      {avatar
        ? <PlayerAvatar color={avatar.color} portraitHint={avatar.emoji} variant={avatar.id} size="settings" />
        : <span className="settings-icon" style={{ backgroundColor: color }} aria-hidden="true">{icon}</span>}
      <span className="settings-row-copy"><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</span>
      <button className="icon-button" type="button" onClick={onEdit} disabled={busy} aria-label={`Modifier ${title}`}>✎</button>
      <button className="icon-button danger" type="button" onClick={() => setConfirming(true)} disabled={busy} aria-label={`Archiver ${title}`}>⌫</button>
      {confirming && <ConfirmDialog title={`Archiver « ${title} » ?`} confirmLabel="Archiver" tone="danger" onCancel={() => setConfirming(false)} onConfirm={() => { setConfirming(false); onArchive(); }}>Masqué des réglages, conservé dans l’historique.</ConfirmDialog>}
    </article>
  );
}

function useDialogFocus(onCancel: () => void, disabled: boolean) {
  const dialogRef = useRef<HTMLElement>(null);
  const cancelRef = useRef(onCancel);
  const disabledRef = useRef(disabled);
  cancelRef.current = onCancel;
  disabledRef.current = disabled;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusable = () => [...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )];
    const initialFocus = dialog.querySelector<HTMLElement>(
      '[autofocus], [data-dialog-initial-focus], input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    ) ?? focusable()[0];
    if (!dialog.contains(document.activeElement)) initialFocus?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !disabledRef.current) {
        event.preventDefault();
        cancelRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }
      const first = elements[0]!;
      const last = elements.at(-1)!;
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return dialogRef;
}

function EditorDialog({ title, onCancel, onSubmit, busy, children }: { title: string; onCancel: () => void; onSubmit: (event: FormEvent) => void; busy: boolean; children: ReactNode }) {
  const dialogRef = useDialogFocus(onCancel, busy);
  return <div className="dialog-backdrop" role="presentation"><section ref={dialogRef} className="dialog editor-dialog" role="dialog" aria-modal="true" aria-labelledby="editor-title"><form onSubmit={onSubmit}><div className="dialog-heading"><h2 id="editor-title">{title}</h2><button className="close-button" type="button" onClick={onCancel} aria-label="Fermer">×</button></div><div className="editor-fields">{children}</div><div className="dialog-actions"><button className="secondary-button" type="button" onClick={onCancel} disabled={busy}>Annuler</button><button className="primary-button" disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer"}</button></div></form></section></div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}

function ConfirmDialog({ title, confirmLabel, onCancel, onConfirm, children, tone = "default", disabled = false }: { title: string; confirmLabel: string; onCancel: () => void; onConfirm: () => void; children: ReactNode; tone?: "default" | "warning" | "danger"; disabled?: boolean }) {
  const dialogRef = useDialogFocus(onCancel, disabled);
  return <div className="dialog-backdrop" role="presentation"><section ref={dialogRef} className={`dialog confirm-dialog tone-${tone}`} role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"><span className="confirm-symbol" aria-hidden="true">{tone === "danger" ? "⌫" : tone === "warning" ? "↯" : "↻"}</span><h2 id="confirm-title">{title}</h2><p>{children}</p><div className="dialog-actions"><button className="secondary-button" type="button" onClick={onCancel} disabled={disabled} data-dialog-initial-focus>Annuler</button><button className={tone === "danger" ? "danger-button" : "primary-button"} type="button" onClick={onConfirm} disabled={disabled}>{confirmLabel}</button></div></section></div>;
}

function Toast({ kind, message, onClose }: { kind: "error" | "notice"; message: string; onClose: () => void }) {
  return <div className={`toast toast-${kind}`} role={kind === "error" ? "alert" : "status"}><span aria-hidden="true">{kind === "error" ? "!" : "✓"}</span><p>{message}</p><button type="button" onClick={onClose} aria-label="Fermer">×</button></div>;
}
